import { yToJS } from './hooks/_yToJS';
import { yMeta, yElements, yLines } from './schema';

const ELEMENT_VALUE_TABLE_KEYS = ['name', 'description', 'color', 'border'];
const ELEMENT_VALUE_NOTE_KEYS  = ['name', 'description', 'color', 'border',
    'alignmentHorizontal', 'alignmentVertical', 'isFontMonospace'];
const ELEMENT_VALUE_PEN_KEYS   = ['color', 'width', 'points', 'newPenId'];

const LINE_VALUE_KEYS = [
    'sourceElementId', 'targetElementId',
    'sourceCardinality', 'targetCardinality',
    'strokeStyle', 'isReversed',
    'lineType', 'waypoints',
    'color', 'description', 'onUpdateTrigger', 'onDeleteTrigger',
];

const AREA_VALUE_KEYS = ['name', 'description', 'color', 'border'];

function pick(obj, keys) {
    const out = {};
    for (const k of keys) if (obj[k] !== undefined) out[k] = obj[k];
    return out;
}

function serializeElement(snapshot) {
    const base = {
        id: snapshot.id, type: snapshot.type,
        x: snapshot.x, y: snapshot.y, width: snapshot.width, height: snapshot.height,
    };
    // User-shrunk display height for column clipping — mirrors `height`,
    // only present while the table is actually clipped.
    if (snapshot.displayHeight !== undefined) base.displayHeight = snapshot.displayHeight;
    let value;
    if (snapshot.type === 'table') {
        value = {
            ...pick(snapshot, ELEMENT_VALUE_TABLE_KEYS),
            areaId: snapshot.areaId ?? null,
            // Re-wrap into legacy { value: { header: { name } } } shape.
            // Highlights are not in the doc; legacy consumers should treat them as absent.
            header: { name: snapshot.name ?? '' },
            columns: snapshot.columnsOrder?.length
                ? Object.fromEntries(snapshot.columnsOrder.map((cid) => {
                    const col = { ...snapshot.columns[cid] };
                    if (col.indexType != null) { col.index = { type: col.indexType }; }
                    delete col.indexType;
                    return [cid, col];
                }))
                : {},
        };
    } else if (snapshot.type === 'note') {
        value = {
            ...pick(snapshot, ELEMENT_VALUE_NOTE_KEYS),
            areaId: snapshot.areaId ?? null,
            text: snapshot.text ?? '',   // re-wrap top-level text into value.text
        };
    } else {
        value = { ...pick(snapshot, ELEMENT_VALUE_PEN_KEYS), areaId: snapshot.areaId ?? null };
    }
    return { ...base, value };
}

function serializeLine(snapshot) {
    const value = pick(snapshot, LINE_VALUE_KEYS);
    const srcColId = snapshot.sourceColumnId ?? snapshot.fromColumnId;
    const tgtColId = snapshot.targetColumnId ?? snapshot.toColumnId;
    if (srcColId !== undefined) value.fromInsideElementId = srcColId;
    if (tgtColId !== undefined) value.toInsideElementId   = tgtColId;
    return { id: snapshot.id, value };
}

function serializeArea(snapshot, doc) {
    const value = pick(snapshot, AREA_VALUE_KEYS);
    // Recompute nestedElementsIds for legacy consumers
    const nestedElementsIds = [];
    yElements(doc).forEach((el, id) => {
        if (el.get('areaId') === snapshot.id && el.get('type') !== 'area') nestedElementsIds.push(id);
    });
    value.nestedElementsIds = nestedElementsIds;
    return {
        id: snapshot.id,
        x: snapshot.x, y: snapshot.y, width: snapshot.width, height: snapshot.height,
        value,
    };
}

export function serializeDoc(doc) {
    const meta = yToJS(yMeta(doc));
    const out = {
        title:       meta.title       ?? '',
        description: meta.description ?? '',
        db_engine:   meta.dbEngine    ?? 'postgresql',
        data: { elements: {}, lines: {}, areas: {} },
    };

    // table elementId -> Set(columnId), used to drop orphaned relations from the
    // export (a line whose endpoint table/column was deleted would otherwise emit a
    // dangling foreign key).
    const tableColumns = new Map();

    yElements(doc).forEach((el, id) => {
        const snap = yToJS(el);
        if (snap.type === 'area') {
            out.data.areas[id] = serializeArea(snap, doc);
        } else {
            out.data.elements[id] = serializeElement(snap);
            if (snap.type === 'table') {
                tableColumns.set(id, new Set(Object.keys(snap.columns ?? {})));
            }
        }
    });

    const endpointValid = (elementId, columnId) => {
        const cols = tableColumns.get(elementId);
        if (!cols) return false;            // endpoint table is gone (or not a table)
        if (columnId == null) return true;  // line doesn't pin a column — element is enough
        return cols.has(columnId);          // a referenced column must still exist
    };

    yLines(doc).forEach((_, id) => {
        const snap = yToJS(yLines(doc).get(id));
        const fromEl  = snap.sourceElementId ?? snap.fromElementId;
        const toEl    = snap.targetElementId ?? snap.toElementId;
        const fromCol = snap.sourceColumnId  ?? snap.fromColumnId;
        const toCol   = snap.targetColumnId  ?? snap.toColumnId;
        if (!endpointValid(fromEl, fromCol) || !endpointValid(toEl, toCol)) {
            return; // skip orphaned line
        }
        out.data.lines[id] = serializeLine(snap);
    });
    return out;
}
